import { IRootReducerState } from 'store/store';
import { IQuestion, QuestionType } from 'types/Question.types';
import {
  formatValuesFromOutputs,
  getValuesForMultipleListAndTableQuestions,
  getValuesForSingleListTableColorAndRadioQuestions,
} from './helpers';
import { Dispatch, useEffect, useRef, useState } from 'react';
import { AnyAction } from 'redux';
import {
  setTriggerShowAddedToCartModal,
  setTriggerShowCartItemEditedModal,
  triggerAddToShoppingCart,
  triggerEditCartItem,
  triggerUpdateQuotationLine,
} from 'store/ShoppingCart/actions/shoppingCart';
import { useNavigate } from 'react-router-dom';
import i18n, { FALLBACK_LOCALE } from 'providers/i18n/i18n';
import { useQuotationLineResolveParams } from 'utils/hooks/useQuotationLineResolveParams';
import { useUpdateQuotationLine } from 'pages/BuyerSingleQuotationPage/hooks';
import {
  setIndicativeQuestionKeys,
  setSubmissionBeingChecked,
  setSubmissionIsSlow,
  setTriggerShowSubmitConfirmedModal,
  triggerSaveTemplate,
  triggerSubmitConfiguration,
} from 'store/SubmitConfiguration/actions/submitConfiguration';
import { useTemplateConfigureParams } from 'utils/hooks/useTemplateConfigureParams';
import {
  useCreateTemplate,
  useUpdateTemplate,
} from 'components/Modals/TemplatesModal/hooks';
import { ITemplateQuestions } from 'types/Template.types';
import {
  HostMessageTypes,
  clearBorrowedSession,
  closeHostAppTab,
  isOpenedFromHostApp,
  postHostMessage,
} from 'utils/hostApp';
import {
  stripEmptyAnswers,
  stripIndicativeFlags,
} from 'providers/QueryProvider/helpers';
import {
  resetConfigureForm,
  setCustomNetPrice,
  setPredefinedPricing,
} from 'store/Model/actions/model';
import { useMutation } from 'react-query';
import { ReactMutationKeys } from 'services/api/reactMutationKeys';
import {
  getToastErrorMessage,
  LogyxError,
  readLogyxError,
} from 'services/api/errors';
import { getIntentStatus, submitConfiguration } from 'services/ExternalService';
import { submitAndWait, submissionOutcomeOf } from './submission';
import { useActiveCartId } from 'utils/hooks/useActiveCartId';
import {
  useAddToShoppingCart,
  useEditShoppingCartItem,
} from 'pages/ShoppingCartPage/hooks';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import {
  IMobileMessage,
  MobileMessageTypes,
  postMobileMessage,
} from 'utils/mobile/postMobileMessage';
import {
  ISelectedConfigurationAnswer,
  ISubmittedExtraPriceAnswer,
} from 'types/ConfigurationQuestion.types';
import { useDispatch, useSelector } from 'react-redux';
import { setManuallyChangedQuestionNames } from 'store/Form/actions/form';
import { useNavigateWithQuery } from 'utils/hooks/useNavigateWithQuery';
import { RoutesConfig } from 'navigation/routes';
import { buildConfiguratorSubPageState } from 'navigation/helpers';
import {
  hasStoredConfiguratorScroll,
  rememberConfiguratorScroll,
} from 'utils/hooks/useConfiguratorScrollMemory';
import {
  useIntentConfiguration,
  useIsEditMode,
} from 'providers/QueryProvider/hooks';
import { AppMode } from 'store/Common/reducers/common';
import { useLocation } from 'react-router-dom';
import {
  IImageSection,
  useFilterOutHiddenImages,
} from 'pages/ConfigurePage/FormImages/hooks';

// Generate values for the form based of question outputs of the query response
// (Or from initial getQuestions response outputs if no query has been made yet)
export const useGetValuesFromQuestionOutputs = (
  cartItemQuestions: IQuestion[] | undefined = undefined,
  isCartValues?: boolean
) => {
  const valuesFromOutputs = {};
  const location = useLocation();
  const storeQuestions = useSelector(
    (state: IRootReducerState) => state.modelInfo.questions
  );
  const { manuallyChangedQuestionNames, manuallyClearedQuestionNames } =
    useSelector((state: IRootReducerState) => state.formInfo);

  const { backFromBomPage } = location.state || {};

  const { intentUUID } = useSelector(
    (state: IRootReducerState) => state.submitConfigurationInfo
  );

  const isEditMode = useIsEditMode(); // Edit with webhook
  const intentConfiguration = useIntentConfiguration(); // External mode

  const currentlyEditingShoppingCartLineId = useSelector(
    (state: IRootReducerState) =>
      state.shoppingCartInfo.currentlyEditingShoppingCartLineId
  );

  const questions = cartItemQuestions || storeQuestions;

  for (const question of questions) {
    if (question.hidden) {
      continue;
    }

    // If it's on Cart page skip default
    // if the question was manually changed/cleared
    // or if the question is currently being edited skip setting default
    // or if is reconfiguration with intentUUID
    // or when user comes back from BOM page in order flow
    const skipSetDefault = !!(
      isCartValues ||
      manuallyChangedQuestionNames.includes(question.initialKey) ||
      manuallyClearedQuestionNames.includes(question.initialKey) ||
      currentlyEditingShoppingCartLineId ||
      (intentUUID && isEditMode) ||
      intentConfiguration ||
      backFromBomPage
    );

    switch (question.type) {
      case QuestionType.BOOLEAN: {
        // Prefer rule-driven outputs when present; fall back to default when the form is fresh
        const outputValue = (question.outputs as { value?: unknown }).value;
        const hasOutputValue =
          outputValue !== undefined && outputValue !== null;

        if (hasOutputValue) {
          valuesFromOutputs[question.initialKey] = !!outputValue;
          break;
        }

        if (!skipSetDefault) {
          // Accept both `default: [true]` (array) and `default: true` (scalar),
          // mirroring how TEXT/NUMBER read defaults.
          const rawDefault = Array.isArray(question.default)
            ? question.default[0]
            : question.default;
          if (rawDefault !== undefined && rawDefault !== null) {
            valuesFromOutputs[question.initialKey] = !!rawDefault;
            break;
          }
        }

        valuesFromOutputs[question.initialKey] = false;
        break;
      }
      case QuestionType.LIST:
        if (question.multiple) {
          valuesFromOutputs[question.initialKey] =
            getValuesForMultipleListAndTableQuestions(question, skipSetDefault);
        } else {
          valuesFromOutputs[question.initialKey] =
            getValuesForSingleListTableColorAndRadioQuestions(
              question,
              skipSetDefault
            );
        }
        break;
      case QuestionType.NUMBER:
        if (question.outputs && question.outputs.value != null) {
          valuesFromOutputs[question.initialKey] = question.outputs.value;
        } else {
          if (skipSetDefault) {
            valuesFromOutputs[question.initialKey] = null;
          } else {
            // If null, handle default value
            valuesFromOutputs[question.initialKey] = Array.isArray(
              question.default
            )
              ? question.default.length
                ? question.default[0]
                : null
              : question.default || null;
          }
        }
        break;
      case QuestionType.TEXT:
        if (
          question.outputs &&
          question.outputs.value !== null &&
          question.outputs.value !== ''
        ) {
          valuesFromOutputs[question.initialKey] = question.outputs.value;
        } else {
          if (skipSetDefault) {
            valuesFromOutputs[question.initialKey] = '';
          } else {
            // If null, handle default value
            valuesFromOutputs[question.initialKey] = Array.isArray(
              question.default
            )
              ? question.default.length
                ? question.default[0]
                : ''
              : question.default || '';
          }
        }
        break;
      case QuestionType.TABLE:
        {
          const isForTable = true;
          if (question.multiple) {
            valuesFromOutputs[question.initialKey] =
              getValuesForMultipleListAndTableQuestions(
                question,
                skipSetDefault,
                isForTable
              );
          } else {
            valuesFromOutputs[question.initialKey] =
              getValuesForSingleListTableColorAndRadioQuestions(
                question,
                skipSetDefault,
                isForTable
              );
          }
        }
        break;
      case QuestionType.RADIO:
        valuesFromOutputs[question.initialKey] =
          getValuesForSingleListTableColorAndRadioQuestions(
            question,
            skipSetDefault
          );
        break;
      case QuestionType.COLOR:
        valuesFromOutputs[question.initialKey] =
          getValuesForSingleListTableColorAndRadioQuestions(
            question,
            skipSetDefault,
            false,
            true
          );
        break;
    }
  }

  return valuesFromOutputs;
};

export const useListenForAddToCartTrigger = (
  queryPayload: object,
  pricing: any,
  dispatch: Dispatch<AnyAction>
) => {
  const {
    triggerAddToShoppingCart: shouldAddCurrentFormValuesToShoppingCart,
    currentlyEditingShoppingCartLineQuantity: cartLineQuantity,
  } = useSelector((state: IRootReducerState) => state.shoppingCartInfo);
  const shoppingCartId = useActiveCartId();

  const appMode: AppMode = useSelector(
    (state: IRootReducerState) => state.commonInfo.appMode
  );

  const { companyId: orderCompanyId } = useSelector(
    (state: IRootReducerState) => state.orderInfo
  );

  const modelInfo = useSelector((state: IRootReducerState) => state.modelInfo);
  const { mutate: addToCart, isSuccess: addToCartSuccess } =
    useAddToShoppingCart();

  useEffect(() => {
    if (addToCartSuccess) {
      dispatch(setTriggerShowAddedToCartModal(true));
      dispatch(setCustomNetPrice(null));
    }
  }, [addToCartSuccess]);

  useEffect(() => {
    if (shouldAddCurrentFormValuesToShoppingCart) {
      addToCart({
        cartLineQuantity,
        modelId: modelInfo.id,
        companyId: appMode === AppMode.SUPPLIER ? orderCompanyId : null,
        lang: modelInfo.lang,
        queryPayload: queryPayload,
        pricing: pricing,
        shoppingCartId: shoppingCartId,
      });
      dispatch(triggerAddToShoppingCart(false));
    }
  }, [shouldAddCurrentFormValuesToShoppingCart]);
};

export const useListenForEditCartItemTrigger = (
  queryPayload: object,
  pricing: any,
  dispatch: Dispatch<AnyAction>
) => {
  const {
    triggerEditCartItem: shouldEditCartItem,
    currentlyEditingShoppingCartLineId: cartLineId,
    currentlyEditingShoppingCartLineQuantity: cartLineQuantity,
  } = useSelector((state: IRootReducerState) => state.shoppingCartInfo);
  const shoppingCartId = useActiveCartId();
  const appMode: AppMode = useSelector(
    (state: IRootReducerState) => state.commonInfo.appMode
  );
  const modelInfo = useSelector((state: IRootReducerState) => state.modelInfo);

  const { companyId: orderCompanyId } = useSelector(
    (state: IRootReducerState) => state.orderInfo
  );

  const { mutate: editCartItem, isSuccess: cartItemEditedSuccess } =
    useEditShoppingCartItem();

  useEffect(() => {
    if (cartItemEditedSuccess) {
      dispatch(setTriggerShowCartItemEditedModal(true));
      dispatch(setCustomNetPrice(null));
    }
  }, [cartItemEditedSuccess]);

  useEffect(() => {
    if (shouldEditCartItem) {
      editCartItem({
        cartLineId,
        cartLineQuantity,
        modelId: modelInfo.id,
        companyId: appMode === AppMode.SUPPLIER ? orderCompanyId : null,
        lang: modelInfo.lang,
        queryPayload: queryPayload,
        pricing,
        shoppingCartId: shoppingCartId,
      });
      dispatch(triggerEditCartItem(false));
    }
  }, [shouldEditCartItem]);
};

// Save of a quotation line resolved in the shared configurator.
export const useListenForUpdateQuotationLineTrigger = (
  queryPayload: { questions: object },
  dispatch: Dispatch<AnyAction>
) => {
  const navigate = useNavigate();
  const resolveParams = useQuotationLineResolveParams();
  const shouldUpdateQuotationLine = useSelector(
    (state: IRootReducerState) =>
      state.shoppingCartInfo.triggerUpdateQuotationLine
  );
  const modelId = useSelector((state: IRootReducerState) => state.modelInfo.id);

  const { mutate: updateQuotationLine } = useUpdateQuotationLine(() => {
    dispatch(setPredefinedPricing(null));
    // The line is definitive now, and this slice is not cleared on rehydrate.
    dispatch(setIndicativeQuestionKeys([]));
    navigate(
      RoutesConfig.BuyerSingleQuotation.fullPath.replace(
        ':id',
        resolveParams?.quotationId ?? ''
      ),
      { replace: true }
    );
  });

  useEffect(() => {
    if (!shouldUpdateQuotationLine) {
      return;
    }
    dispatch(triggerUpdateQuotationLine(false));
    if (!resolveParams || !modelId) {
      return;
    }
    updateQuotationLine({
      quotationId: resolveParams.quotationId,
      quotationLineId: resolveParams.lineId,
      dto: {
        model_id: modelId,
        questions: stripIndicativeFlags(queryPayload.questions),
        language_code: i18n.resolvedLanguage || FALLBACK_LOCALE,
      },
    });
  }, [shouldUpdateQuotationLine]);
};

export const useScrollToFirstIndicativeQuestion = (hasQuestions: boolean) => {
  const isResolvingQuotationLine = !!useQuotationLineResolveParams();
  const hasScrolledRef = useRef<boolean>(false);

  useEffect(() => {
    if (!isResolvingQuotationLine || !hasQuestions || hasScrolledRef.current) {
      return;
    }
    // Returning from BOM or Specs: the user already dealt with this form once,
    // so put them back where they left it rather than at the indicative
    // question again. Runs before the restore effect, which is declared after
    // this hook in ConfigureForm and so has not consumed the offset yet.
    if (hasStoredConfiguratorScroll()) {
      hasScrolledRef.current = true;
      return;
    }
    const firstIndicativeRow = document.querySelector('[data-needs-resolving]');
    if (firstIndicativeRow) {
      hasScrolledRef.current = true;
      firstIndicativeRow.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [isResolvingQuotationLine, hasQuestions]);
};

export const useGetSortedQuestions = () => {
  const initialQuestions = useSelector(
    (state: IRootReducerState) => state.modelInfo.questions
  );
  const sortedQuestions = [...initialQuestions].sort((a, b) => {
    const orderA = a.order !== undefined ? a.order : Infinity;
    const orderB = b.order !== undefined ? b.order : Infinity;
    return orderA - orderB;
  });
  return sortedQuestions;
};

export interface IConfiguredProductData {
  modelId: number | null;
  versionNumber: number | null;
  pricing: object | null;
  roundPricing: boolean;
  questions: IQuestion[];
  general: object | null;
  valuesFromOutputs: object;
  lang: string;
  queryPayload: object; // Payload that was last sent to query the model before form submission
  // Answered extra price questions (sent on submit, returned by get-edit-configuration).
  extra_price_questions?: ISubmittedExtraPriceAnswer[];
  // Legacy single extra price question fields, kept only so that previously-saved
  // configurations can still be read (best-effort) when re-opened for editing.
  extraPriceQuestionName?: string;
  selectedExtraPriceQuestionOption?: { label: string; value: number };
  images?: IImageSection[]; // Image layers for configuration_images generation
}

export const useListenForSubmitConfigurationTrigger = (
  valuesFromOutputs: object,
  dispatch: Dispatch<AnyAction>
) => {
  const isFormValid = useSelector(
    (state: IRootReducerState) => state.formInfo.isFormValid
  );
  const {
    triggerSubmitConfiguration: shouldSubmitConfiguration,
    base64Metadata,
    selectedConfigurationAnswers,
  } = useSelector((state: IRootReducerState) => state.submitConfigurationInfo);
  const modelInfo = useSelector((state: IRootReducerState) => state.modelInfo);
  const isQueryRequestAttempt = useSelector(
    (state: IRootReducerState) => state.modelInfo.queryStatus.attempt
  );
  const { mutate: submitConfiguration, isSuccess: submitConfigurationSuccess } =
    useSubmitConfigurationWithWebhook();

  // Get visible images for configuration_images generation
  const visibleImages: IImageSection[] = useFilterOutHiddenImages(
    modelInfo.images
  );

  useEffect(() => {
    if (isFormValid && shouldSubmitConfiguration) {
      // Only answered (filled-in) extra price questions are sent, in the
      // backend's slug-based shape.
      const extra_price_questions: ISubmittedExtraPriceAnswer[] =
        selectedConfigurationAnswers.map(
          (answer: ISelectedConfigurationAnswer) => ({
            label: answer.questionLabel,
            slug: answer.questionSlug,
            selected_option: {
              label: answer.optionLabel,
              price: answer.price,
              slug: answer.optionSlug,
            },
          })
        );
      const configurationData: IConfiguredProductData = {
        modelId: modelInfo.id,
        versionNumber: modelInfo.versionNumber,
        pricing: modelInfo.pricing,
        roundPricing: modelInfo.roundPricing,
        questions: modelInfo.questions,
        general: modelInfo.general,
        valuesFromOutputs: formatValuesFromOutputs(
          valuesFromOutputs,
          modelInfo.questions
        ),
        lang: modelInfo.lang,
        queryPayload: modelInfo.queryPayload,
        images: visibleImages, // Image layers for configuration_images generation
        ...(extra_price_questions.length && { extra_price_questions }),
      };
      const dto: ISubmitConfigurationWithWebhookDTO = {
        data: configurationData,
        base64Metadata,
      };
      submitConfiguration(dto);
      dispatch(triggerSubmitConfiguration(false));
    }
  }, [shouldSubmitConfiguration, isQueryRequestAttempt]);

  const isMobileApp = useSelector(
    (state: IRootReducerState) => state.commonInfo.isMobileApp
  );

  useEffect(() => {
    if (submitConfigurationSuccess) {
      if (isMobileApp) {
        const mobileMessage: IMobileMessage = {
          type: MobileMessageTypes.CLOSE_LOGYX,
        };
        postMobileMessage(mobileMessage);
      }
      window.close();
      dispatch(setTriggerShowSubmitConfirmedModal(true));
      dispatch(resetConfigureForm());
      dispatch(setCustomNetPrice(null));
    }
  }, [submitConfigurationSuccess]);
};

export interface ISubmitConfigurationWithWebhookDTO {
  data: IConfiguredProductData;
  base64Metadata?: string;
}

// Submits the configuration and follows it until Logyx core settles it
// (./submission.ts). No retry: a submission is never sent twice from here.
const useSubmitConfigurationWithWebhook = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  return useMutation(
    (dto: ISubmitConfigurationWithWebhookDTO) =>
      submitAndWait(
        { submit: submitConfiguration, getStatus: getIntentStatus },
        dto.data,
        dto.base64Metadata,
        () => dispatch(setSubmissionIsSlow(true))
      ),
    {
      onSuccess: () => {
        // Success handled in useListenForSubmitConfigurationTrigger
      },
      onError: (error: unknown) => {
        const outcome = submissionOutcomeOf(error);
        if (outcome?.kind === 'being-checked') {
          dispatch(
            setSubmissionBeingChecked({ message: outcome.serverMessage })
          );
        } else if (outcome?.kind === 'failed') {
          toast.error(
            outcome.serverMessage || t('Failed to submit configuration')
          );
        } else if (outcome?.kind === 'not-received') {
          toast.error(
            t('The submission did not reach us. Please submit it again.')
          );
        } else {
          const answer = readLogyxError(error);
          const message = answer.data?.message;
          toast.error(
            typeof message === 'string' && message
              ? message
              : answer.isLogyxAnswer
              ? getToastErrorMessage(error as LogyxError)
              : t('Failed to submit configuration')
          );
        }
      },
      onSettled: () => {
        dispatch(setSubmissionIsSlow(false));
      },
      mutationKey: ReactMutationKeys.SUBMIT_CONFIGURATION_WITH_WEBHOOK,
    }
  );
};

// If value is changed manually, add it to the store
export const useSetManuallyChangeQuestionName = (
  dispatch: Dispatch<AnyAction>
) => {
  return (newValue: any, questionKey: string) => {
    if (newValue !== undefined && newValue !== null) {
      dispatch(setManuallyChangedQuestionNames(questionKey));
    }
  };
};

// includeBomItems triggers a query inside QueryProvider, and once that gets caught in this useEffect, the query request has been triggered because of didValuesChange, and we navigate to BomPage
// hasJustClickedIncludeBomItems will be set to false once the user remounts ConfigureForm (comes back from BomPage), which will result in navigation not triggering again, even if includeBomItems value didnt compute yet as false in ConfigureForm
export const useHandleNavigateToBomPage = () => {
  const { includeBomItems } = useSelector(
    (state: IRootReducerState) => state.formInfo
  );
  const navigate = useNavigateWithQuery();
  const location = useLocation();
  const [hasJustClickedIncludeBomItems, setHasJustClickedIncludeBomItems] =
    useState<boolean>(false);
  const modelId = useSelector((state: IRootReducerState) => state.modelInfo.id);
  useEffect(() => {
    if (includeBomItems && hasJustClickedIncludeBomItems) {
      rememberConfiguratorScroll(modelId);
      navigate(RoutesConfig.Bom.fullPath, {
        state: buildConfiguratorSubPageState(location),
      });
    }
  }, [includeBomItems]);
  return { setHasJustClickedIncludeBomItems };
};

// Saving the current configuration as a quotation template. Mounted inside
// QueryProvider like the cart and quotation-line listeners so it reads the
// freshest queryPayload rather than the last one that made it into redux.
export const useListenForSaveTemplateTrigger = (
  queryPayload: { questions: object },
  dispatch: Dispatch<AnyAction>
) => {
  const navigateWithQuery = useNavigateWithQuery();
  const location = useLocation();
  const templateParams = useTemplateConfigureParams();
  const saveRequest = useSelector(
    (state: IRootReducerState) =>
      state.submitConfigurationInfo.triggerSaveTemplate
  );
  const modelId = useSelector((state: IRootReducerState) => state.modelInfo.id);

  const navigateBackToModels = () =>
    navigateWithQuery(
      RoutesConfig.DealerPanelModels.fullPath,
      { state: location.state, replace: true },
      undefined,
      ['modelId', 'isTemplate', 'templateId']
    );

  // Opened in a tab by another app (the admin panel) purely to build a template:
  // hand the result back and close, rather than navigating to a dealer panel the
  // user may have no business on. Falls through to the normal navigation when
  // there is no opener, so the dealer panel flow is untouched.
  const handleTemplateSaved = (templateId?: number) => {
    if (!isOpenedFromHostApp()) {
      navigateBackToModels();
      return;
    }
    postHostMessage({
      type: HostMessageTypes.TEMPLATE_SAVED,
      payload: { templateId },
    });
    // Clear only on the way out: while the tab is open it still needs its token.
    clearBorrowedSession();
    closeHostAppTab();
  };

  const { mutate: createTemplate } = useCreateTemplate((template) =>
    handleTemplateSaved(template?.id)
  );
  const { mutate: updateTemplate } = useUpdateTemplate((template) =>
    handleTemplateSaved(template?.id)
  );

  useEffect(() => {
    if (!saveRequest) {
      return;
    }
    dispatch(triggerSaveTemplate(null));
    if (!templateParams || !modelId) {
      return;
    }

    // Templates hold answers only: the indicative marks belong to a quotation
    // line rather than the preset, and unanswered questions are dropped instead
    // of being stored as an explicit empty answer.
    const questions = stripEmptyAnswers(
      stripIndicativeFlags(queryPayload.questions)
    ) as ITemplateQuestions;

    if (templateParams.templateId) {
      updateTemplate({
        templateId: Number(templateParams.templateId),
        dto: {
          questions,
          ...(saveRequest.name && { name: saveRequest.name }),
        },
      });
    } else if (saveRequest.name) {
      createTemplate({
        model_id: Number(modelId),
        name: saveRequest.name,
        questions,
      });
    }
  }, [saveRequest]);
};
